/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Icon, Typography } from '@saviynt/design-system';

import CircleIcon from '../../../../components/illustrations/CircleIcon';
import NestedIcon from '../../../../components/illustrations/NestedIcon';
import { getActualStepName } from '../../constants/CuaOnboardintConstants';
import { type CopilotState } from '../../data-layer/types';
import { initializeOwnerKey } from '../../data-store/copilotSlice';
import useAutoScroll from '../../hooks/useAutoScroll';
import { useFileSubmit } from '../../hooks/useFileSubmit';
import { useFullscreenRedux } from '../../hooks/useFullscreenRedux';
import useNavigationData from '../../hooks/useNavigationData';
import { useStepRefs } from '../../hooks/useStepRefs';
import ErrorBanner from '../ErrorBanner/ErrorBanner';
import ExpandedFormView from '../ExpandedFormView/ExpandedFormView';
import Header from '../Header/Header';
import LeftSidebar from '../LeftSidebar/LeftSidebar';
import MainContent from '../MainContent/MainContent';
import PromptFooter from '../PromptFooter/PromptFooter';
import SaveDraftModalIsland from '../SaveDraftModalIsland/SaveDraftModalIsland';

import './CopilotContainer.css';

const { Item } = Grid;

interface AppState {
  copilot: CopilotState;
  user?: {
    userInfo?: {
      username?: string;
      id?: string;
    };
  };
}

/**
 * Main Copilot container with responsive layout
 */
function CopilotContainer() {
  const dispatch = useDispatch();
  const { containerRef } = useAutoScroll();
  const { stepRefs, scrollToStep } = useStepRefs();
  const { isFullscreen, fullScreenStep } = useFullscreenRedux();

  const { navigation, isLoading, isError } = useNavigationData();
  const { completedSteps } = navigation;

  const isCompleted = useCallback(
    (stepName: string) => completedSteps.includes(stepName),
    [completedSteps]
  );

  // Initialize file submission hook
  const { handleFileSubmit } = useFileSubmit();

  // Memoize error banner style to prevent object recreation
  const errorBannerStyle = useMemo(() => ({ marginTop: '3rem' }), []);

  const { promptFooterEnabled, username, userId } = useSelector(
    (state: AppState) => ({
      promptFooterEnabled: state.copilot.ui.promptFooterEnabled,
      username: state.user?.userInfo?.username,
      userId: state.user?.userInfo?.id,
    })
  );

  // Initialize owner key with username
  useEffect(() => {
    if (username && userId) {
      dispatch(initializeOwnerKey({ userId: String(userId), username }));
    }
  }, [username, userId, dispatch]);

  return (
    <Grid spacing={0} className='Copilot-Grid'>
      {/* Left Navigation */}
      {!isFullscreen && (
        <Item desktop={2} tablet={12} className='Copilot-ChatGrid-LeftNav'>
          <Box className='Copilot-NavContent'>
            {isError ? (
              <div style={errorBannerStyle}>
                <ErrorBanner message='An error occurred. Please try later...' />
              </div>
            ) : (
              <MemoizedLeftSidebar
                scrollToStep={scrollToStep}
                navigation={navigation}
                isLoading={isLoading}
                isError={isError}
              />
            )}
          </Box>
        </Item>
      )}

      {/* Main Content Area */}
      <Item
        desktop={isFullscreen ? 5 : 10}
        tablet={12}
        className='Copilot-ChatGrid-Main'>
        <Header />
        <>
          {/* Normal Chat View */}
          {!isFullscreen && (
            <div className='Copilot-Step-Container'>
              <MemoizedMainContent
                containerRef={containerRef}
                stepRefs={stepRefs}
                scrollToStep={scrollToStep}
              />
            </div>
          )}
          {/* Fullscreen Left Panel */}
          {isFullscreen && (
            <div className='Copilot-Fullscreen-Content'>
              <NestedIcon />
              <Box className='Copilot-Fullscreen-Step'>
                {isCompleted(getActualStepName(fullScreenStep)) ? (
                  <Icon
                    kind='CheckCircleFilledSeparateColors'
                    color='--color-foreground-success-subtle'
                    size='smallMedium'
                  />
                ) : (
                  <CircleIcon />
                )}

                <Typography kind='h3' desktop='h2-d' mobile='h2-d'>
                  {fullScreenStep}
                </Typography>
              </Box>
            </div>
          )}

          <PromptFooter
            isDisabled={!promptFooterEnabled}
            onFileSubmit={handleFileSubmit}
          />
        </>
      </Item>

      {/* Expanded Form View */}
      {isFullscreen && (
        <Item desktop={7} tablet={12} className='Copilot-ChatGrid-Fullscreen'>
          <ExpandedFormView />
        </Item>
      )}

      <SaveDraftModalIsland scrollToStep={scrollToStep} />
    </Grid>
  );
}

// Memoized components to prevent unnecessary re-renders
const MemoizedLeftSidebar = React.memo(LeftSidebar);
const MemoizedMainContent = React.memo(MainContent);

export default CopilotContainer;
